import { gql } from '@api/gql';

/**
 * Single order's billing/coverage snapshots + patient + status. `coverages`
 * is the full ranked set (primary, secondary, …) captured on the order.
 */
export const GET_ORDER_BILLING = gql(/* GraphQL */`
  query GetOrderBilling($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      order_id
      status
      patient {
        patient_id
      }
      coverages {
        order_coverage_id
        coverage_id
        payor_name
        policy_number
        plan_type
        coverage_order
        kind
      }
    }
  }
`);

export const GET_SERVICE_REQUEST = gql(/* GraphQL */`
  query GetServiceRequest($options: QueryOptionsInputType, $searchBy: SearchByArgs) {
    getServiceRequest(options: $options, searchBy: $searchBy) {
      ...ServiceRequestCoreFragment
      requisition {
        ...IdentifierCoreFragment
      }
      reports {
        ...DiagnosticReportCoreFragment
      }
    }
  }
`);

export const GET_ALL_SERVICE_REQUESTS = gql(/* GraphQL */`
  query GetAllServiceRequests($searchBy: SearchByArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getAllServiceRequests(searchBy: $searchBy, sort: $sort, options: $options) {
      ...ServiceRequestCoreFragment
      requisition {
        ...IdentifierCoreFragment
      }
      performer {
        ... on Organizations {
          organization_id
          alias
          preferred_member
        }
      },

      location {
        __typename
        location_id
        alias
      }
    }
  }
`);

export const GET_PAGINATED_SERVICE_REQUESTS = gql(/* GraphQL */`
  query GetPaginatedServiceRequests($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedServiceRequests(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      totalPages
      currentPage
      rows {
        ...ServiceRequestCoreFragment
        requisition {
          ...IdentifierCoreFragment
        }
        patient {
          __typename
          patient_id
          user {
            __typename
            user_id
            demographics {
              ...DemographicsCoreFragmentAlt
            }
          }
        }
        performer {
          ... on Organizations {
            organization_id
            alias
            preferred_member
          }
          ... on Locations {
            location_id
            name
            preferred_member
          }
        }
        reports {
          __typename
          report_id
          code
          presented_form {
            __typename
            attachment_id
            url
          }
          flags {
            __typename
            flag_id
            hl7_code
            alert_priority
            display
          }
        }
        location {
          __typename
          location_id
          alias
        }
      }
    }
  }
`);

export const GET_ORDER = gql(/* GraphQL */`
  query GetOrder($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      series {
        ...EventSeriesCoreFragment
        pattern {
          ...RecurrencePatternCoreFragment
        }
      }
    }
  }
`);

export const GET_ORDER_SPECIMEN = gql(/* GraphQL */`
  query GetOrderSpecimen($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
    }
  }
`);

export const GET_ORDER_DETAILS = gql(/* GraphQL */`
  query OrderDetails($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      diagnosis_codes {
        ...ICD10CodeFragment
      }
      contacts {
        ...ContactInformationFragment
      }
      patient {
        ...PatientCoreFragment
      }
      agents {
        ...OrderAgentCoreFragment
        actor {
          ...OrderAgentActorCoreFragment
        }
      }
      series {
        ...EventSeriesCoreFragment
        pattern {
          ...RecurrencePatternCoreFragment
        }
      }
      reports {
        __typename
        report_id
        request_id
        code
        status
        presented_form {
          ...AttachmentCoreFragment
        }
      }
      requisition {
        reference_id
        content {
          ...AttachmentCoreFragment
        }
      }
      flags {
        __typename
        flag_id
        hl7_code
        alert_priority
        display
      }
    }
  }
`);

export const ORDER_REQUISITION_DOCUMENT = gql(/* GraphQL */`
  query OrderRequisitionDocument($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrderDetails(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      requisition {
        reference_id
        content {
          attachment_id
          content_type
          title
          url
          data
        }
      }
    }
  }
`);

export const ORDER_REQUISITION_DATA = gql(/* GraphQL */`
  query OrderRequisitionData($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrderDetails(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      diagnosis_codes {
        ...ICD10CoreFragment
      }
      requests {
        request_id
        code
      }
      patient {
        ...PatientCoreFragment
        user {
          user_id
          demographics {
            cynergy_id
            first_name
            last_name
            full_name
            dob
            sex
          }
        }
      }
      requisition {
        reference_id
        content {
          attachment_id
          title
          url
        }
      }
      signature {
        order_id
        practitioner_id
        name
        practitioner {
          practitioner_id
          npi
          demographics {
            cynergy_id
            first_name
            last_name
            full_name
          }
        }
        signature {
          signature_id
          url
        }
      }
    }
  }
`);

export const ORDER_TIMELINE = gql(/* GraphQL */`
  query OrderTimeline($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      created_at
    }
  }
`);

export const ORDER_DIAGNOSIS_CODES = gql(/* GraphQL */`
  query OrderDiagnosisCodes($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      patient {
        patient_id
      }
      line_items {
        line_item_id
      }
      diagnosis_codes {
        ...ICD10CodeFragment
      }
    }
  }
`);

export const GET_PAGINATED_ORDER_ITEMS = gql(/* GraphQL */`
  query GetPaginatedOrderItems($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedOrderItems(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      currentPage
      totalPages
      rows {
        ...OrderLineItemCoreFragment
        request {
          ...ServiceRequestCoreFragment
          service {
            service_id
          }
          location {
            __typename
            location_id
            alias
            phone
          }
        }
      }
    }
  }
`);

export const ORDER_NOTES_FEED = gql(/* GraphQL */`
  query OrderNotesFeed($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedOrderNotes(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      currentPage
      totalPages
      rows {
        annotation_id
        text
        created_at
        author {
          user_id
          demographics {
            cynergy_id
            full_name
          }
        }
      }
    }
  }
`);

export const GET_COMPLETED_PATIENT_ORDERS = gql(/* GraphQL */`
  query GetCompletedPatientOrders($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedOrders(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      rows {
        ...OrderCoreFragment
        requests {
          __typename
          request_id
          code
          status
          reports {
            __typename
            report_id
            code
            result {
              __typename
              observation_id
              code
              interpretation
            }
            flags {
              __typename
              flag_id
              hl7_code
              alert_priority
              display
              observation {
                __typename
                observation_id
                interpretation
                code
              }
            }
          }
        }
      }
    }
  }
`);

export const GET_PAGINATED_ORDERS = gql(/* GraphQL */`
  query GetPaginatedOrders($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedOrders(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      currentPage
      totalPages
      rows {
        ...OrderCoreFragment
        patient {
          __typename
          patient_id
          user {
            __typename
            user_id
            identity {
              ...CynergyAccountCoreFragmentAlt
              addresses {
                __typename
                address_id
                room
              }
            }
            demographics {
              ...DemographicsCoreFragmentAlt
            }
          }
        }
        requisition {
          ...DocumentReferenceCoreFragment
          content {
            __typename
            attachment_id
            url
            title
          }
        }
        flags {
          __typename
          flag_id
          hl7_code
          alert_priority
          display
        }
        requests {
          __typename
          request_id
          reports {
            __typename
            report_id
            code
            presented_form {
              __typename
              attachment_id
              url
            }
          }
        }
      }
    }
  }
`);

export const ORDER_STATISTIC = gql(/* GraphQL */`
  query OrderStatistics($parameters: OrderStatisticsInput!, $searchBy: SearchByArgs) {
    orderStatistics(parameters: $parameters, searchBy: $searchBy) {
      field
      stats {
        count
        value
      }
    }
  }
`);

export const ORDER_SIGNATURE = gql(/* GraphQL */`
  query GetOrderSignature($orderId: String!) {
    getOrderSignature(orderId: $orderId) {
      order_id
      signature_id
      name
      practitioner_id
      practitioner {
        practitioner_id
        npi
      }
      signature {
        signature_id
        signature_format
        signed_by
        url
      }
    }
  }
`);

export const DOWNLOAD_CSV_ORDERS_REPORT = gql(/* GraphQL */`
  query DownloadCSVOrdersReport($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    downloadCSVOrdersReport(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) { url }
  }
`);

export const GET_PAGINATED_RECURRING_ORDERS = gql(/* GraphQL */`
  query RecurringOrders($searchBy: SearchByArgs, $pagination: PaginationArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getPaginatedRecurringOrders(searchBy: $searchBy, pagination: $pagination, sort: $sort, options: $options) {
      count
      currentPage
      totalPages
      rows {
        order_id
        series_id
        event_id
        title
        subject_name
        scheduled_date
        next_scheduled_date
        created_at
        status
        recurs
        deleted_at
        patient {
          patient_id
          user {
            user_id
            demographics {
              cynergy_id
              first_name
              last_name
              full_name
              dob
            }
          }
        }
        series {
          series_id
          start_time
          deleted_at
          created_at
          pattern {
            pattern_id
            text
            start_date
            end_date
          }
        }
      }
    }
  }
`);


export const GET_RECENT_ORDERS = gql(/* GraphQL */`
  query GetRecentOrders($searchBy: SearchByArgs, $sort: SortingArgs, $options: QueryOptionsInputType) {
    getAllOrders(searchBy: $searchBy, sort: $sort, options: $options) {
      order_id
      title
      description
      status
      priority
      scheduled_date
      subject_name
      fulfilled_at
      created_at
    }
  }
`);

export const GET_ORDER_LINE_ITEM = gql(/* GraphQL */`
  query GetOrderLineItem($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrderLineItem(searchBy: $searchBy, options: $options) {
      ...OrderLineItemCoreFragment
      order {
        order_id
      }
      request {
        ...ServiceRequestCoreFragment
      }
    }
  }
`);

export const GET_ORDER_RENEWAL_SOURCE = gql(/* GraphQL */`
  query GetOrderRenewalSource($searchBy: SearchByArgs, $options: QueryOptionsInputType) {
    getOrder(searchBy: $searchBy, options: $options) {
      ...OrderCoreFragment
      patient {
        patient_id
      }
      diagnosis_codes {
        ...ICD10CodeFragment
      }
      line_items {
        line_item_id
        store_id
        pricebook_entry_id
        type_code
        request {
          ...ServiceRequestCoreFragment
          service {
            service_id
          }
          location {
            location_id
          }
        }
      }
      signature {
        order_id
        signature_id
        practitioner_id
        name
        practitioner {
          practitioner_id
          npi
          demographics {
            cynergy_id
            full_name
            first_name
            last_name
          }
        }
        signature {
          signature_id
          url
        }
      }
      series {
        ...EventSeriesCoreFragment
        pattern {
          ...RecurrencePatternCoreFragment
          weekly {
            ...RecurrencePatternWeeklyCoreFragment
          }
          monthly {
            ...RecurrencePatternMonthlyCoreFragment
          }
        }
      }
    }
  }
`);
